import { useEffect, useRef } from 'react'
import { useEffectReducer } from 'use-effect-reducer'
import {
  CNP_EVENT,
  INTEGRATION_TYPE,
  PAYMENT_STATUS,
  TerminalEventMonitoring,
} from '@lightspeed-payments/payments-terminal-sdk'
import { useVdFeatureManager } from '../../../../../ng-compat/hooks'
import { SANDBOX_FLAG, TRANSACTION_TYPE } from '../../helpers/lspayConstants'
import { getMonitoringInstance } from '../../helpers/terminalMonitoring'
import { getCnpPmofSessionPaymentEffectsMap } from './effects'
import { cnpPmofPaymentStateReducer } from './reducer'
import { LSPAYCnpPmofPaymentState, LSPAYCnpPmofPaymentHookArgs } from './types'

export const useCnpPmofSessionEffectReducer = (
  args: LSPAYCnpPmofPaymentHookArgs
) => {
  const isSandbox = useVdFeatureManager().get(SANDBOX_FLAG)
  const terminalMonitoringInstanceRef = useRef<TerminalEventMonitoring | null>(
    null
  )
  if (
    !terminalMonitoringInstanceRef.current &&
    !args.disableTerminalMonitoring
  ) {
    terminalMonitoringInstanceRef.current = getMonitoringInstance(
      args.outletId,
      isSandbox,
      INTEGRATION_TYPE.MANUAL
    )
  }

  const completeFiredRef = useRef(false)

  const [state, dispatch] = useEffectReducer(
    cnpPmofPaymentStateReducer,
    exec => {
      exec({ type: 'createSessionEffect' })
      const initialState: LSPAYCnpPmofPaymentState = {
        status: 'creatingSession',
      }
      return initialState
    },
    getCnpPmofSessionPaymentEffectsMap({
      ...args,
      terminalMonitoringInstance: terminalMonitoringInstanceRef.current,
      onCompleteFired: () => {
        completeFiredRef.current = true
      },
    })
  )

  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    return () => {
      const { status } = stateRef.current
      // Send COMPLETE if the payment was abandoned mid-flow (e.g. tab switch, modal close).
      if (
        !completeFiredRef.current &&
        status !== 'done' &&
        status !== 'error' &&
        status !== 'confirmingPaymentStateTimeout'
      ) {
        terminalMonitoringInstanceRef.current?.sendEvent(CNP_EVENT.COMPLETE, {
          payment: {
            status: PAYMENT_STATUS.INCOMPLETE,
            reason: 'Payment was interrupted',
          },
          transactionType: TRANSACTION_TYPE.PAYMENT,
        })
      }
    }
  }, [])

  return [state, dispatch] as const
}
